/**
 * Journey map — built from the Vision Board.
 *
 * The 12-month timeline on /journey is the membership cadence (assessment,
 * reflections, refreshes). That is *when* WCC360 checks in with you.
 *
 * This module builds the other half: *your* map — the dreams you pinned on the
 * Vision Board and the action steps you set under each one. Same source of
 * truth the board reads: `vision_boards` for the durable copy, the local
 * cloudStorage cache for an instant, offline-friendly first paint.
 */
import { supabase } from "@/integrations/supabase/client";
import { cloudStorage } from "@/lib/cloudStore";

const LS = {
  dreams: "wekit:vb:dreams:v1",
  actions: "wekit:vb:actions:v1",
  daily: "wekit:vb:daily:v1",
  streak: "wekit:vb:streak:v1",
};

export type VbDream = {
  id: string;
  title: string;
  note?: string;
  targetYear?: number;
  priority?: "low" | "medium" | "high";
  tags?: string[];
  createdAt: number;
};

export type VbActionStep = {
  id: string;
  label: string;
  xp: number;
  difficulty: 1 | 2 | 3;
  hours: number;
  done: boolean;
};

export type VbActionPlan = { dreamId: string; dreamTitle: string; steps: VbActionStep[] };

export type VisionBoardState = {
  dreams: VbDream[];
  actions: VbActionPlan[];
  daily: { date: string; tasks: { label: string; done: boolean }[] } | null;
  streak: { count: number; last: string } | null;
};

function readLocal<T>(key: string, fallback: T): T {
  try {
    const raw = cloudStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

/** Local-first read so the map paints instantly, before the network answers. */
export function readLocalVisionBoard(): VisionBoardState {
  return {
    dreams: readLocal<VbDream[]>(LS.dreams, []),
    actions: readLocal<VbActionPlan[]>(LS.actions, []),
    daily: readLocal<VisionBoardState["daily"]>(LS.daily, null),
    streak: readLocal<VisionBoardState["streak"]>(LS.streak, null),
  };
}

/** Durable read — the board as saved to your account, on any device. */
export async function fetchVisionBoard(): Promise<VisionBoardState> {
  const local = readLocalVisionBoard();
  const { data: auth } = await supabase.auth.getUser();
  const uid = auth?.user?.id;
  if (!uid) return local;

  const { data } = await supabase
    .from("vision_boards")
    .select("dreams,actions,daily,streak")
    .eq("user_id", uid)
    .maybeSingle();
  if (!data) return local;

  const dreams = Array.isArray(data.dreams) ? (data.dreams as unknown as VbDream[]) : [];
  const actions = Array.isArray(data.actions) ? (data.actions as unknown as VbActionPlan[]) : [];
  return {
    dreams: dreams.length ? dreams : local.dreams,
    actions: actions.length ? actions : local.actions,
    daily: (data.daily as VisionBoardState["daily"]) ?? local.daily,
    streak: (data.streak as VisionBoardState["streak"]) ?? local.streak,
  };
}

export type JourneyMapStop = {
  dreamId: string;
  title: string;
  note?: string;
  targetYear?: number;
  priority: "low" | "medium" | "high";
  /** ci_id when this stop came from a career pinned in the explorer. */
  careerId?: string;
  steps: VbActionStep[];
  doneCount: number;
  totalSteps: number;
  progressPct: number;
  nextStep?: VbActionStep;
  status: "not_started" | "in_progress" | "achieved";
};

export type JourneyMap = {
  stops: JourneyMapStop[];
  totalSteps: number;
  doneSteps: number;
  progressPct: number;
  xpEarned: number;
  xpAvailable: number;
  hoursRemaining: number;
  dailyDone: number;
  dailyTotal: number;
  streak: number;
};

const PRIORITY_RANK = { high: 0, medium: 1, low: 2 } as const;

/**
 * Order of the map: nearest target year first, then priority, then the order
 * you pinned them. A dream without a target year sits after the dated ones —
 * it is still on the map, just not on the clock yet.
 */
export function buildJourneyMap(vb: VisionBoardState): JourneyMap {
  const byDream = new Map(vb.actions.map((a) => [a.dreamId, a]));

  const stops: JourneyMapStop[] = vb.dreams.map((d) => {
    const steps = byDream.get(d.id)?.steps ?? [];
    const doneCount = steps.filter((s) => s.done).length;
    const totalSteps = steps.length;
    const careerTag = (d.tags ?? []).find((t) => t.startsWith("career:"));
    return {
      dreamId: d.id,
      title: d.title,
      note: d.note,
      targetYear: d.targetYear,
      priority: d.priority ?? "medium",
      careerId: careerTag?.slice("career:".length),
      steps,
      doneCount,
      totalSteps,
      progressPct: totalSteps ? Math.round((doneCount / totalSteps) * 100) : 0,
      nextStep: steps.find((s) => !s.done),
      status:
        totalSteps > 0 && doneCount === totalSteps
          ? "achieved"
          : doneCount > 0
            ? "in_progress"
            : "not_started",
    };
  });

  stops.sort((a, b) => {
    const ay = a.targetYear ?? Number.POSITIVE_INFINITY;
    const by = b.targetYear ?? Number.POSITIVE_INFINITY;
    if (ay !== by) return ay - by;
    const ap = PRIORITY_RANK[a.priority];
    const bp = PRIORITY_RANK[b.priority];
    if (ap !== bp) return ap - bp;
    return a.title.localeCompare(b.title);
  });

  const allSteps = stops.flatMap((s) => s.steps);
  const doneSteps = allSteps.filter((s) => s.done).length;

  return {
    stops,
    totalSteps: allSteps.length,
    doneSteps,
    progressPct: allSteps.length ? Math.round((doneSteps / allSteps.length) * 100) : 0,
    xpEarned: allSteps.filter((s) => s.done).reduce((n, s) => n + (s.xp || 0), 0),
    xpAvailable: allSteps.reduce((n, s) => n + (s.xp || 0), 0),
    hoursRemaining: allSteps.filter((s) => !s.done).reduce((n, s) => n + (s.hours || 0), 0),
    dailyDone: vb.daily?.tasks.filter((t) => t.done).length ?? 0,
    dailyTotal: vb.daily?.tasks.length ?? 0,
    streak: vb.streak?.count ?? 0,
  };
}
